import React, { useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
    'p', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'ul', 'ol', 'li', 'blockquote', 'h2', 'h3', 'h4', 'br', 'span'
];

/**
 * Converts legacy markdown (e.g. **bold**, *italic*, lists) to clean HTML.
 */
export const convertMarkdownToHTML = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    // If it already has HTML tags, return it
    if (/<(p|strong|b|em|i|u|ul|ol|li|blockquote|h[1-6]|br)[^>]*>/i.test(text)) {
        return DOMPurify.sanitize(text, { ALLOWED_TAGS });
    }

    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Italic: *text* (avoiding inner tags)
    html = html.replace(/(^|[^\*])\*(?!\s)(.*?)(?!\s)\*(?!\*)/g, '$1<em>$2</em>');

    // Blockquote: > text
    html = html.replace(/^>\s+(.*$)/gim, '<blockquote>$1</blockquote>');

    // Headings
    html = html.replace(/^###\s+(.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^##\s+(.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^#\s+(.*$)/gim, '<h2>$1</h2>');

    // Lists (- item or • item)
    html = html.replace(/^[-*•]\s+(.*$)/gim, '<li>$1</li>');
    html = html.replace(/^\d+[\.\)]\s+(.*$)/gim, '<li>$1</li>');

    // Group adjacent <li> into <ul>
    html = html.replace(/(<li>.*?<\/li>(\s*<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');

    // Wrap paragraphs and line breaks
    const blocks = html.split(/\n{2,}/);
    html = blocks.map(b => {
        const trimmed = b.trim();
        if (!trimmed) return '';
        if (/^<(ul|ol|blockquote|h[1-6])/.test(trimmed)) {
            return trimmed;
        }
        return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
    }).filter(Boolean).join('');

    return DOMPurify.sanitize(html || text, { ALLOWED_TAGS });
};

/**
 * WYSIWYG Rich Text Editor for clinical and diary notes.
 * Directly formats text as bold, italic, lists, and quotes inside the editor without raw asterisks.
 */
const RichTextEditor = ({
    value = '',
    onChange,
    placeholder = 'Scrivi qui le tue osservazioni o note...',
    minHeight = '150px',
    className = ''
}) => {
    const editorRef = useRef(null);
    const lastHtmlRef = useRef('');

    // Synchronize value into editor when it changes externally (and user is not currently typing)
    useEffect(() => {
        if (!editorRef.current) return;
        const targetHtml = convertMarkdownToHTML(value);

        if (document.activeElement !== editorRef.current) {
            if (editorRef.current.innerHTML !== targetHtml) {
                editorRef.current.innerHTML = targetHtml;
                lastHtmlRef.current = targetHtml;
            }
        }
    }, [value]);

    const handleInput = useCallback(() => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        // Clean empty divs/breaks
        const clean = (html === '<p><br></p>' || html === '<br>' || html === '<div><br></div>') ? '' : html;
        lastHtmlRef.current = clean;
        if (onChange) {
            onChange(clean);
        }
    }, [onChange]);

    const executeCommand = (command, val = null) => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        document.execCommand(command, false, val);
        handleInput();
    };

    return (
        <div className={`border-2 border-slate-200 rounded-2xl overflow-hidden bg-white shadow-2xs focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 transition-all ${className}`}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200 text-xs select-none">
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('bold'); }}
                    className="w-7 h-7 flex items-center justify-center font-black text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition"
                    title="Grassetto (B)"
                >
                    B
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('italic'); }}
                    className="w-7 h-7 flex items-center justify-center italic font-serif text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition"
                    title="Corsivo (I)"
                >
                    I
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('underline'); }}
                    className="w-7 h-7 flex items-center justify-center underline font-medium text-slate-800 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition"
                    title="Sottolineato (U)"
                >
                    U
                </button>

                <div className="w-px h-5 bg-slate-200 mx-1" />

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('insertUnorderedList'); }}
                    className="px-2.5 h-7 flex items-center gap-1.5 text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition font-medium"
                    title="Elenco puntato"
                >
                    <span>•</span> Lista
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('insertOrderedList'); }}
                    className="px-2.5 h-7 flex items-center gap-1.5 text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition font-medium"
                    title="Elenco numerato"
                >
                    <span>1.</span> Numerata
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('formatBlock', 'blockquote'); }}
                    className="px-2.5 h-7 flex items-center gap-1 text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition font-serif"
                    title="Citazione / Vissuto"
                >
                    “ Citazione
                </button>
                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('formatBlock', 'h3'); }}
                    className="px-2.5 h-7 flex items-center gap-1 text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition font-bold"
                    title="Titolo sezione (H2)"
                >
                    H2
                </button>

                <div className="w-px h-5 bg-slate-200 mx-1" />

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeCommand('removeFormat'); }}
                    className="px-2 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
                    title="Rimuovi formattazione"
                >
                    ✕ Rimuovi
                </button>
            </div>

            {/* Editable Content Area */}
            <div className="relative">
                <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleInput}
                    onBlur={handleInput}
                    style={{ minHeight }}
                    className="p-3.5 sm:p-4 text-xs sm:text-sm text-slate-800 leading-relaxed outline-none focus:outline-none overflow-y-auto max-h-[450px] prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-blockquote:my-1.5 prose-blockquote:border-l-3 prose-blockquote:border-indigo-400 prose-blockquote:bg-indigo-50/40 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:rounded-r-lg prose-blockquote:italic"
                />

                {/* Placeholder when empty */}
                {(!value || value === '<p><br></p>' || value === '<br>') && (
                    <div
                        onClick={() => editorRef.current && editorRef.current.focus()}
                        className="absolute top-4 left-4 text-slate-400 text-xs sm:text-sm pointer-events-none select-none italic"
                    >
                        {placeholder}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RichTextEditor;
